import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { StartChatTarget } from '@chat-emp/shared';

import { startChatApi } from '../lib/facade.js';
import { clearDraft, draftKeyFor, readDraft, writeDraft } from './composeDraft.js';

export interface UseCompose {
  draft: string;
  setDraft: (text: string) => void;
  /**
   * Create the channel and post the message as one call. Resolves to the id of
   * the conversation that now exists, or null if nothing was sent.
   *
   * Takes the text rather than reading `draft`, because the send fires from the
   * same event that produces the final keystroke: a closure over state would be
   * one character behind, and the character it drops is the one they typed
   * immediately before hitting Enter.
   */
  send: (text: string) => Promise<string | null>;
  sending: boolean;
  error: string | null;
}

/**
 * Writing to someone the chat system has never heard of.
 *
 * There is no channel behind this state and there will not be one until the
 * employer sends — that is the whole point. Under the old flow the host created
 * a channel first, so anyone who thought better of it left an empty conversation
 * behind and every CTA for that person flipped to "View Chat" pointing at
 * nothing.
 */
/**
 * `employerId` is the Stream user id, which is only known once the client has
 * connected — and the panel can open onto a compose before that. An empty id
 * means "no stable key yet": the draft lives in state only, rather than being
 * written under a key that changes out from under it the moment Stream connects.
 * Sending never depends on it.
 */
export function useCompose(target: StartChatTarget | null, employerId: string): UseCompose {
  const key = useMemo(
    () => (target && employerId ? draftKeyFor(employerId, target) : null),
    [employerId, target],
  );
  const [draft, setDraftState] = useState(() => (key ? readDraft(key) : ''));
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // A ref, not `sending`: a double-click lands both handlers inside one React
  // batch, so state set by the first has not committed when the second reads it.
  const inFlight = useRef(false);

  useEffect(() => {
    setError(null);
    setDraftState(key ? readDraft(key) : '');
  }, [key]);

  const setDraft = useCallback(
    (text: string) => {
      setDraftState(text);
      if (key) writeDraft(key, text);
    },
    [key],
  );

  const send = useCallback(
    async (raw: string): Promise<string | null> => {
      // Note: no `key` requirement. Persistence is a convenience; being unable to
      // save a draft must never stop someone sending the message.
      if (!target) return null;
      const text = raw.trim();
      if (text === '' || inFlight.current) return null;

      inFlight.current = true;
      setSending(true);
      setError(null);
      try {
        const { channelId } = await startChatApi({
          applicant: target.applicant,
          company: target.company,
          source: target.source,
          ...(target.sourceMeta ? { sourceMeta: target.sourceMeta } : {}),
          ...(target.job ? { job: target.job } : {}),
          text,
        });
        // Only now, once the message is somewhere durable. Clearing optimistically
        // and then failing would lose the one copy of what they wrote.
        if (key) clearDraft(key);
        setDraftState('');
        return channelId;
      } catch {
        setError('Could not send. Your message is saved, try again.');
        return null;
      } finally {
        inFlight.current = false;
        setSending(false);
      }
    },
    [key, target],
  );

  return { draft, setDraft, send, sending, error };
}
